// API client with auth token handling and automatic refresh on 401

const BASE_URL = process.env.REACT_APP_API_BASE || '';

const TOKEN_KEY = 'sb-access-token';
const REFRESH_KEY = 'sb-refresh-token';
const USER_KEY = 'sb-user';

export const tokenStorage = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY));
    } catch {
      return null;
    }
  },
  save: ({ accessToken, refreshToken, user }) => {
    if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

let refreshPromise = null;

async function refreshTokens() {
  // De-dupe concurrent refresh attempts
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = tokenStorage.getRefresh();
    if (!refreshToken) throw new Error('No refresh token');

    const res = await fetch(`${BASE_URL}/api/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) throw new Error('Refresh failed');

    const json = await res.json();
    const data = json.data || json;
    tokenStorage.save({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });
    return data.accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// Core request function with 401 → refresh → retry
export async function apiFetch(path, options = {}, _isRetry = false) {
  const token = tokenStorage.getAccess();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401 && !_isRetry) {
    try {
      await refreshTokens();
      return apiFetch(path, options, true);
    } catch {
      tokenStorage.clear();
      // Signal to the app that auth is gone
      window.dispatchEvent(new Event('sb-auth-expired'));
      throw new ApiError('Session expired. Please log in again.', 401);
    }
  }

  let body = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON response
  }

  if (!res.ok) {
    const message = body?.error || body?.message || `Request failed (${res.status})`;
    throw new ApiError(message, res.status, body);
  }

  return body;
}

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// ── Auth API ──
export const authApi = {
  login: async (email, password) => {
    const json = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    const data = json.data || json;
    tokenStorage.save(data);
    return data;
  },

  register: async (username, email, password) => {
    const json = await apiFetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });
    const data = json.data || json;
    tokenStorage.save(data);
    return data;
  },

  logout: () => {
    tokenStorage.clear();
  },
};

// ── User Sentences API ──
export const sentencesApi = {
  list: async () => {
    const json = await apiFetch('/api/user-sentences');
    return json.sentences || json.data?.sentences || [];
  },

  create: async ({ chinese, pinyin, english, notes, breakdown }) => {
    const json = await apiFetch('/api/user-sentences', {
      method: 'POST',
      body: JSON.stringify({ chinese, pinyin, english, notes, breakdown }),
    });
    return json.sentence || json.data || json;
  },

  update: async (id, fields) => {
    const json = await apiFetch(`/api/user-sentences/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    return json.sentence || json.data || json;
  },

  remove: async (id) => {
    return apiFetch(`/api/user-sentences/${id}`, { method: 'DELETE' });
  },
};
